import React, { useState, useEffect, useMemo } from 'react';
import { Trash2, User as UserIcon, X, Tag as TagIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const COLUMNS = [
  { key: 'todo', label: 'Yeni' },
  { key: 'in_progress', label: 'İcra olunur' },
  { key: 'review', label: 'Təhvilə hazırdır' },
  { key: 'blocked', label: 'Problemli' },
  { key: 'done', label: 'Təhvil alındı' },
];

/**
 * Shared Project Item (Mərhələ) edit dialog — used by Kanban and Gantt tabs.
 */
export default function ProjectItemDialog({
  open,
  item,
  members,
  allItems,
  canEdit,
  onClose,
  onSave,
  onDelete,
}) {
  const [form, setForm] = useState(null);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (open && item) {
      setForm({
        title: item.title || '',
        description: item.description || '',
        status: item.status || 'todo',
        priority: item.priority || 'medium',
        assignee_ids: item.assignee_ids || [],
        start_date: item.start_date || '',
        due_date: item.due_date || '',
        tags: item.tags || [],
        parent_id: item.parent_id || '',
      });
      setTagInput('');
    }
  }, [open, item]);

  const forbiddenParents = useMemo(() => {
    if (!item?.id || !allItems) return new Set();
    const forbidden = new Set([item.id]);
    const queue = [item.id];
    while (queue.length) {
      const cur = queue.shift();
      allItems.forEach((x) => {
        if (x.parent_id === cur && !forbidden.has(x.id)) {
          forbidden.add(x.id);
          queue.push(x.id);
        }
      });
    }
    return forbidden;
  }, [item?.id, allItems]);

  const parentCandidates = useMemo(() => {
    return (allItems || []).filter((x) => !forbiddenParents.has(x.id) && !x.parent_id);
  }, [allItems, forbiddenParents]);

  if (!form) return null;

  const toggleAssignee = (id) => {
    setForm((f) => ({
      ...f,
      assignee_ids: f.assignee_ids.includes(id)
        ? f.assignee_ids.filter((x) => x !== id)
        : [...f.assignee_ids, id],
    }));
  };

  const addTag = () => {
    const v = tagInput.trim();
    if (!v) return;
    if (form.tags.includes(v)) {
      setTagInput('');
      return;
    }
    setForm({ ...form, tags: [...form.tags, v] });
    setTagInput('');
  };

  const removeTag = (t) => {
    setForm({ ...form, tags: form.tags.filter((x) => x !== t) });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="kanban-item-dialog">
        <DialogHeader>
          <DialogTitle>{item?.id ? 'Mərhələ redaktəsi' : 'Yeni mərhələ'}</DialogTitle>
          <DialogDescription className="sr-only">
            Layihə mərhələsinin yaradılması və redaktəsi.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <label className="text-sm font-medium">Başlıq *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Mərhələnin adı"
              data-testid="item-title-input"
              disabled={!canEdit}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Açıqlama</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              disabled={!canEdit}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
                disabled={!canEdit}
              >
                <SelectTrigger data-testid="item-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLUMNS.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Prioritet</label>
              <Select
                value={form.priority}
                onValueChange={(v) => setForm({ ...form, priority: v })}
                disabled={!canEdit}
              >
                <SelectTrigger data-testid="item-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Aşağı</SelectItem>
                  <SelectItem value="medium">Orta</SelectItem>
                  <SelectItem value="high">Yüksək</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Başlanma</label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                disabled={!canEdit}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Bitmə</label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Üst mərhələ (parent)</label>
            <Select
              value={form.parent_id || 'none'}
              onValueChange={(v) => setForm({ ...form, parent_id: v === 'none' ? '' : v })}
              disabled={!canEdit}
            >
              <SelectTrigger data-testid="item-parent-select">
                <SelectValue placeholder="Yoxdur (root mərhələ)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Yoxdur (root) —</SelectItem>
                {parentCandidates.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {item?.id && parentCandidates.length === 0 && (
              <div className="text-xs text-zinc-400 mt-1 italic">
                Üst kimi seçilə bilən başqa root mərhələ yoxdur
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Məsul şəxslər (layihə üzvlərindən)</label>
            <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1 mt-1">
              {members.length === 0 && (
                <div className="text-xs text-zinc-400 italic">Layihə üzvü yoxdur</div>
              )}
              {members.map((m) => {
                const selected = form.assignee_ids.includes(m.id);
                return (
                  <label
                    key={m.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-zinc-50 ${
                      selected ? 'bg-purple-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleAssignee(m.id)}
                      disabled={!canEdit}
                      data-testid={`assignee-checkbox-${m.id}`}
                    />
                    <UserIcon className="h-3.5 w-3.5 text-zinc-400" />
                    <span className="text-sm flex-1 truncate">{m.full_name || m.username}</span>
                    <span className="text-[10px] text-zinc-500">@{m.username}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Etiketlər</label>
            <div className="flex gap-1 flex-wrap mt-1 mb-2">
              {form.tags.map((t) => (
                <Badge key={t} variant="secondary" className="gap-1">
                  {t}
                  {canEdit && (
                    <button onClick={() => removeTag(t)} className="hover:text-red-600">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Etiket əlavə et və Enter"
                  className="text-sm"
                />
                <Button size="sm" variant="outline" onClick={addTag}>
                  <TagIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          {canEdit && item?.id && (
            <Button
              variant="outline"
              onClick={() => onDelete(item)}
              className="text-red-600 hover:text-red-700 mr-auto"
              data-testid="delete-item-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Sil
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Ləğv et
          </Button>
          {canEdit && (
            <Button
              onClick={() => onSave(form)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-item-btn"
            >
              Yadda saxla
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
